import ELK from 'elkjs/lib/elk.bundled.js'
import { Edge, MarkerType, Node } from 'reactflow'
import { WorkflowConfig, WorkflowEdge, WorkflowNode } from 'sprites-types/workflow/core'

export type FlowchartNode = WorkflowNode & { visualNode?: boolean }

// ELK layout function
export const layoutWithELK = async (
  workflowData: WorkflowConfig,
  measuredHeights?: Record<string, number>
): Promise<{ nodes: Node[]; edges: Edge[] }> => {
  console.debug('[LAYOUT] Starting ELK layout for workflow:', {
    workflowId: workflowData.id,
    workflowName: workflowData.name,
    nodeCount: workflowData.nodes.length,
    startNode: workflowData.start,
    hasOutputs: !!workflowData.outputs,
  })

  const elk = new ELK()

  // Validate workflow data and collect edge issues
  const nodeIds = new Set(workflowData.nodes.map((n) => n.id))
  console.debug('[LAYOUT] Node IDs in workflow:', Array.from(nodeIds))
  const invalidEdges = new Set<string>()

  // Check for invalid edge targets before processing
  workflowData.nodes.forEach((node) => {
    console.log(`[LAYOUT] Processing edges for node "${node.id}":`, {
      edgeCount: node.edges.length,
      edges: node.edges.map((e) => ({
        tag: e.tag,
        target: e.target,
        isTerminal: 'terminal' in e,
        terminal: 'terminal' in e ? (e as any).terminal : undefined,
      })),
    })

    node.edges.forEach((edge) => {
      if ('target' in edge && edge.target && !nodeIds.has(edge.target)) {
        invalidEdges.add(`${node.id}->${edge.target}`)
        console.warn(
          `[LAYOUT] Invalid edge found: Node "${node.id}" points to non-existent node "${edge.target}". This edge will be treated as terminal.`
        )
      } else if ('terminal' in edge) {
        console.log(`[LAYOUT] Terminal edge found in node "${node.id}":`, {
          tag: edge.tag,
          terminal: (edge as any).terminal,
          target: edge.target,
        })
      }
    })
  })

  // Build nodes for ELK
  const nodes: Node[] = []

  // Add start node
  nodes.push({
    id: '__start__',
    type: 'flowchartNode',
    position: { x: 0, y: 0 },
    selectable: false,
    data: {
      id: '__start__',
      name: 'Start',
      description: 'Begin workflow execution',
      toolId: 'start',
      edges: [],
      inputs: {},
      visualNode: true,
    } satisfies FlowchartNode,
  })

  // Track terminal nodes (nodes with no edges or with terminal edges)
  const terminalNodeIds = new Set<string>()
  console.debug('[LAYOUT] Starting terminal node detection...')

  // Add flowchart nodes
  workflowData.nodes.forEach((node: WorkflowNode) => {
    nodes.push({
      id: node.id,
      type: 'flowchartNode',
      position: { x: 0, y: 0 },
      data: {
        ...node,
      },
    })

    // Check if this is a terminal node

    if (!node.edges || node.edges.length === 0) {
      // Node with no edges is terminal
      terminalNodeIds.add(node.id)
      console.log(`[LAYOUT] Node "${node.id}" is terminal (no edges)`)
    } else {
      // Check for terminal edges (edges without targets) and invalid edges
      const hasValidNonTerminalEdge = node.edges.some(
        (edge) => edge.target && nodeIds.has(edge.target)
      )
      console.log(
        `[LAYOUT] Node "${node.id}" has valid non-terminal edge:`,
        hasValidNonTerminalEdge
      )

      if (!hasValidNonTerminalEdge) {
        // All edges are either terminal or invalid - treat as terminal
        terminalNodeIds.add(node.id)
        console.log(`[LAYOUT] Node "${node.id}" is terminal (all edges are terminal or invalid)`)
      }
    }
  })

  // Add visual-only end node if there are terminal nodes
  console.debug('[LAYOUT] Terminal nodes found:', Array.from(terminalNodeIds))

  if (terminalNodeIds.size > 0) {
    console.debug('[LAYOUT] Adding visual end node for terminal nodes')
    nodes.push({
      id: '__end__',
      type: 'flowchartNode',
      position: { x: 0, y: 0 },
      selectable: false,
      data: {
        id: '__end__',
        name: 'End',
        description: 'Workflow execution complete',
        toolId: 'end',
        edges: [],
        inputs: {},
        visualNode: true,
      } satisfies FlowchartNode,
    })
  }

  // Support string | string[] for workflowData.start
  const startIds = Array.isArray(workflowData.start) ? workflowData.start : [workflowData.start]
  const validStartIds = startIds.filter((id) => nodeIds.has(id))

  // Find orphaned nodes (nodes with no incoming edges that aren't explicitly start nodes)
  const nodesWithIncomingEdges = new Set<string>()
  workflowData.nodes.forEach((node) => {
    node.edges.forEach((edge) => {
      if (edge.target && nodeIds.has(edge.target)) {
        nodesWithIncomingEdges.add(edge.target)
      }
    })
  })

  const orphanedNodes = workflowData.nodes
    .filter((node) => !nodesWithIncomingEdges.has(node.id) && !validStartIds.includes(node.id))
    .map((node) => node.id)

  // Combine explicit start nodes with orphaned nodes
  const effectiveStartIds = [...validStartIds, ...orphanedNodes]

  console.debug('[LAYOUT] Start nodes:', {
    requested: startIds,
    valid: validStartIds,
    orphaned: orphanedNodes,
    effective: effectiveStartIds,
  })

  // Build all edges (start edge + workflow edges) for BFS
  const allEdges: { source: string; target: string }[] = []

  if (effectiveStartIds.length === 0) {
    // No valid start found, fallback to first available node
    if (startIds.length > 0) {
      console.warn(
        `Start node(s) "${startIds.join(', ')}" do not exist. Layout will use first available node or create empty layout.`
      )
    }
    const firstNode = workflowData.nodes[0]
    if (firstNode) {
      allEdges.push({ source: '__start__', target: firstNode.id })
    }
  } else {
    effectiveStartIds.forEach((startId) => {
      allEdges.push({ source: '__start__', target: startId })
    })
  }

  workflowData.nodes.forEach((node: WorkflowNode) => {
    node.edges.forEach((edge: WorkflowEdge) => {
      // Only add edges that have valid targets
      if (edge.target && nodeIds.has(edge.target)) {
        allEdges.push({ source: node.id, target: edge.target })
        console.log(`[LAYOUT] Added edge: ${node.id} -> ${edge.target}`)
      } else if (!edge.target) {
        console.log(`[LAYOUT] Skipping terminal edge from node "${node.id}" (null target)`)
      } else {
        console.log(`[LAYOUT] Skipping invalid edge from node "${node.id}" to "${edge.target}"`)
      }
    })
  })

  // Add edges from terminal nodes to the visual end node
  if (terminalNodeIds.size > 0) {
    terminalNodeIds.forEach((nodeId) => {
      allEdges.push({ source: nodeId, target: '__end__' })
    })
  }

  // Compute nodeLayers ensuring merging nodes are below ALL their parents
  const nodeLayers: Record<string, number> = {}
  const incomingEdges: Record<string, string[]> = {}

  // Build incoming edges map
  allEdges.forEach((edge) => {
    if (!incomingEdges[edge.target]) {
      incomingEdges[edge.target] = []
    }
    incomingEdges[edge.target].push(edge.source)
  })

  // Initialize with start node
  nodeLayers['__start__'] = 0
  console.debug('[LAYOUT] Starting BFS layer calculation...')

  // Keep processing until all nodes have layers assigned
  let changed = true
  let iteration = 0
  const maxIterations = 100 // Prevent infinite loops

  while (changed && iteration < maxIterations) {
    iteration++
    changed = false

    allEdges.forEach((edge) => {
      const { source, target } = edge

      // Skip if source doesn't have a layer yet
      if (!(source in nodeLayers)) return

      // Get all parent nodes for this target
      const parents = incomingEdges[target] || []

      // Check if all parents have been assigned layers
      const allParentsAssigned = parents.every((parent) => parent in nodeLayers)

      if (allParentsAssigned) {
        // Calculate the maximum layer among all parents + 1
        const maxParentLayer = Math.max(...parents.map((parent) => nodeLayers[parent]))
        const newLayer = maxParentLayer + 1

        // Update layer if not assigned or if we found a deeper requirement
        if (!(target in nodeLayers) || nodeLayers[target] < newLayer) {
          console.log(
            `[LAYOUT] Iteration ${iteration}: Assigning layer ${newLayer} to node "${target}"`
          )
          nodeLayers[target] = newLayer
          changed = true
        }
      }
    })
  }

  // Check for unassigned nodes
  const unassignedNodes = Array.from(nodeIds).filter((id) => !(id in nodeLayers))
  if (unassignedNodes.length > 0) {
    console.warn('[LAYOUT] WARNING: Some nodes were not assigned layers:', unassignedNodes)
    console.warn(
      '[LAYOUT] This usually means there are disconnected subgraphs or circular dependencies'
    )

    // Assign these nodes to layer 1 as a fallback
    unassignedNodes.forEach((nodeId) => {
      nodeLayers[nodeId] = 1
      console.log(`[LAYOUT] Fallback: Assigning layer 1 to disconnected node "${nodeId}"`)
    })
  }

  // Force end node to be on the deepest layer + 1
  if (terminalNodeIds.size > 0) {
    const maxLayer = Math.max(...Object.values(nodeLayers))
    nodeLayers['__end__'] = maxLayer + 1
    console.log(`[LAYOUT] Placed end node at layer ${maxLayer + 1}`)
  }

  console.debug('[LAYOUT] Final node layers:', nodeLayers)

  // Now build the React Flow edges array, using nodeLayers for isLoop detection and styling
  const edges: Edge[] = []

  // Visual edges from __start__ to all valid start nodes (or fallback if none)
  if (validStartIds.length > 0) {
    validStartIds.forEach((startId) => {
      edges.push({
        id: '__start__-to-' + startId,
        source: '__start__',
        target: startId,
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' },
        animated: false,
      })
    })
  } else {
    const firstNode = workflowData.nodes[0]
    if (firstNode) {
      edges.push({
        id: '__start__-to-' + firstNode.id,
        source: '__start__',
        target: firstNode.id,
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' },
        animated: false,
      })
    }
  }

  workflowData.nodes.forEach((node: WorkflowNode, nodeIndex: number) => {
    // Count visible edges (edges with valid, non-null targets) for this node
    const visibleEdgesCount = node.edges.filter(
      (edge) => edge.target && nodeIds.has(edge.target)
    ).length

    console.log(`[LAYOUT] Processing edges for React Flow - Node "${node.id}":`, {
      totalEdges: node.edges.length,
      visibleEdges: visibleEdgesCount,
    })

    node.edges.forEach((edge: WorkflowEdge, edgeIndex: number) => {
      // Skip edges with null targets (terminal edges) or invalid targets
      if (!edge.target) {
        console.log(`[LAYOUT] Skipping terminal edge from "${node.id}" (tag: ${edge.tag})`)
        return // Skip terminal edges (null targets)
      }
      if (!nodeIds.has(edge.target)) {
        console.log(`[LAYOUT] Skipping invalid edge from "${node.id}" to "${edge.target}"`)
        return // Skip invalid targets
      }

      const sourceLayer = nodeLayers[node.id] ?? 0
      const targetLayer = nodeLayers[edge.target] ?? 0
      const isLoop = targetLayer <= sourceLayer

      let sourceHandle = 'bottom'
      let targetHandle = 'top'

      if (isLoop) {
        // For loop edges, choose left or right based on relative position and edge index
        // This helps distribute loop edges and avoid overlaps
        const useRightSide = (nodeIndex + edgeIndex) % 2 === 0
        sourceHandle = useRightSide ? 'right' : 'left'
        targetHandle = useRightSide ? 'right' : 'left'
      }

      edges.push({
        id: `${node.id}-${edge.target}-${edgeIndex}`,
        source: node.id,
        target: edge.target,
        sourceHandle,
        targetHandle,
        type: 'custom',
        animated: isLoop,
        style: {
          stroke: isLoop
            ? '#f59e0b'
            : 'edges' in node && node.edges && visibleEdgesCount > 1
              ? '#3b82f6'
              : '#94a3b8',
          strokeWidth: isLoop
            ? 2
            : 'edges' in node && node.edges && visibleEdgesCount > 1
              ? 1.5
              : 1,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isLoop
            ? '#f59e0b'
            : 'edges' in node && node.edges && visibleEdgesCount > 1
              ? '#3b82f6'
              : '#94a3b8',
          width: 15,
          height: 15,
        },
        // Add edge data for custom label handling
        data: {
          tag: edge.tag,
          description: edge.description,
          condition: edge.condition,
          hasMultipleEdges: visibleEdgesCount > 1,
        },
      })
    })
  })

  if (terminalNodeIds.size > 0) {
    terminalNodeIds.forEach((nodeId) => {
      const hasOtherEdges = edges.some((e) => e.source === nodeId && e.target !== '__end__')
      if (hasOtherEdges) {
        return
      }

      edges.push({
        id: `${nodeId}-to-__end__`,
        source: nodeId,
        target: '__end__',
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'default',
        style: { stroke: '#ef4444', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#ef4444' },
        animated: false,
      })
    })
  }

  const nodeIdSet = new Set(nodes.map((n) => n.id))
  const validEdges = edges.filter((edge) => {
    const hasValidSource = nodeIdSet.has(edge.source)
    const hasValidTarget = nodeIdSet.has(edge.target)
    return hasValidSource && hasValidTarget
  })

  const seenEdgeIds = new Set<string>()
  const deduplicatedEdges = validEdges.filter((edge) => {
    if (seenEdgeIds.has(edge.id)) {
      return false
    }
    seenEdgeIds.add(edge.id)
    return true
  })

  const elkEdges = deduplicatedEdges.map((edge: Edge) => {
    const sourceLayer = nodeLayers[edge.source] ?? 0
    const targetLayer = nodeLayers[edge.target] ?? 0
    const isLoop = targetLayer <= sourceLayer
    return {
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
      ...(isLoop ? { 'elk.layered.feedbackEdge': true } : {}),
    }
  })

  // Sort nodes by BFS layer (and by id for stable order within layers)
  const sortedNodes = [...nodes].sort((a, b) => {
    const layerA = nodeLayers[a.id] ?? 0
    const layerB = nodeLayers[b.id] ?? 0
    if (layerA !== layerB) return layerA - layerB
    return a.id.localeCompare(b.id)
  })

  const elkNodes = sortedNodes.map((node) => {
    const width = 320 // Match actual rendered width from FlowchartNode component
    let height: number
    if (measuredHeights && measuredHeights[node.id]) {
      height = measuredHeights[node.id]
    } else {
      // Improved fallback height estimation matching FlowchartNode.tsx structure
      let estimatedHeight = 0

      // Header section: p-5 pb-4 (20px left+right, 20px top, 16px bottom)
      // Contains: h-10 icon (40px) + text-base title (~20px with line-height)
      estimatedHeight += 20 + 16 // padding top + bottom
      estimatedHeight += Math.max(40, 20) // max of icon height and title height

      // Tool section: px-5 pb-3 (only if not visual node)
      const hasToolSection = node.data.toolId && node.data.visualNode !== true
      if (hasToolSection) {
        estimatedHeight += 12 // pb-3 bottom padding
        // Inner div: px-4 py-2.5 with text-xs content
        estimatedHeight += 5 + 5 + 18 // py-2.5 top + bottom + text height (slightly increased)
      }

      // Description section: px-5 pb-4
      const hasDescription = node.data.description && node.data.description.length > 0
      if (hasDescription) {
        estimatedHeight += 16 // pb-4 bottom padding
        // line-clamp-3 with text-sm (14px) and leading-relaxed (1.625)
        const lineHeight = 14 * 1.625
        const descriptionLines = Math.min(3, Math.ceil((node.data.description?.length || 0) / 50))
        estimatedHeight += Math.ceil(lineHeight * descriptionLines)
      }

      // Start node buttons section: px-5 pb-4 (if start node)
      if (node.id === '__start__') {
        estimatedHeight += 16 + 36 // pb-4 + button height
      }

      // Execution details section estimate (border-t + px-5 pb-4 pt-4)
      // This is a rough estimate as we don't know execution state at layout time
      // Most nodes will get measured before this matters

      // Border (2px total)
      estimatedHeight += 2

      height = Math.max(120, estimatedHeight) // Minimum height
    }
    const elkNode: any = {
      id: node.id,
      width,
      height,
    }
    if (node.id === '__start__') {
      elkNode['elk.layered.layering.layerConstraint'] = 'FIRST'
    }
    if (node.id === '__end__') {
      elkNode['elk.layered.layering.layerConstraint'] = 'LAST'
    }
    return elkNode
  })

  // Group nodes by layer and calculate max height per layer
  const nodesByLayer: Record<number, any[]> = {}
  elkNodes.forEach((node) => {
    const layer = nodeLayers[node.id] ?? 0
    if (!nodesByLayer[layer]) {
      nodesByLayer[layer] = []
    }
    nodesByLayer[layer].push(node)
  })

  // Calculate maximum height for each layer
  const layerMaxHeights: Record<number, number> = {}
  Object.keys(nodesByLayer).forEach((layerStr) => {
    const layer = parseInt(layerStr)
    layerMaxHeights[layer] = Math.max(...nodesByLayer[layer].map((node) => node.height))
  })

  const elkGraph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'DOWN',
      'elk.spacing.nodeNode': '60', // Horizontal spacing between sibling nodes
      'elk.layered.spacing.nodeNodeBetweenLayers': '80', // Vertical spacing between layers
      'elk.spacing.edgeNode': '20',
      'elk.padding': '[top=40,left=40,bottom=40,right=40]',
      'elk.edgeRouting': 'SPLINES',
      'elk.layered.mergeEdges': 'false',
      'elk.spacing.edgeEdge': '20',
      'elk.layered.spacing.edgeEdgeBetweenLayers': '15',
      'elk.layered.cycleBreaking.strategy': 'MODEL_ORDER',
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'elk.layered.crossingMinimization.semiInteractive': 'true',
      'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
      'elk.partitioning.activate': 'false',
      'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF', // Better for tree-like structures
      'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED', // Center nodes under parents
      'elk.layered.nodePlacement.bk.edgeStraightening': 'IMPROVE_STRAIGHTNESS', // Straighten edges
      'elk.layered.compaction.postCompaction.strategy': 'LEFT_RIGHT_CONSTRAINT_LOCKING', // Balanced compaction
      'elk.layered.layering.strategy': 'LONGEST_PATH', // Better for tree-like branching
      'elk.layered.layering.nodePromotion': 'false',
      'elk.spacing.componentComponent': '80',
      'elk.alignment': 'CENTER', // Center alignment for balanced look
      'elk.layered.thoroughness': '10', // Higher quality layout (1-10 scale)
    },
    children: elkNodes,
    edges: elkEdges,
  }

  console.debug('[LAYOUT] Submitting graph to ELK with:', {
    nodeCount: elkNodes.length,
    edgeCount: elkEdges.length,
    algorithm: elkGraph.layoutOptions['elk.algorithm'],
  })

  try {
    const layoutedGraph = await elk.layout(elkGraph)
    console.debug('[LAYOUT] ELK layout successful')

    // Apply positions to nodes
    const layoutedNodes = nodes.map((node) => {
      const elkNode = layoutedGraph.children?.find((n: any) => n.id === node.id)
      if (!elkNode) return node

      return {
        ...node,
        position: {
          x: elkNode.x || 0,
          y: elkNode.y || 0,
        },
      }
    })

    // Use ELK's positions directly - ELK already handles optimal positioning
    const adjustedNodes = layoutedNodes

    // Adjust loop edge handles based on node positions
    const minX = Math.min(...adjustedNodes.map((n) => n.position.x))
    const maxX = Math.max(...adjustedNodes.map((n) => n.position.x))
    const graphMidX = (minX + maxX) / 2

    const adjustedEdges = deduplicatedEdges.map((edge) => {
      if (edge.animated) {
        // loop edge
        const sourceNode = adjustedNodes.find((n) => n.id === edge.source)
        const targetNode = adjustedNodes.find((n) => n.id === edge.target)
        if (sourceNode && targetNode) {
          if (sourceNode.position.x <= graphMidX && targetNode.position.x <= graphMidX) {
            return { ...edge, sourceHandle: 'left', targetHandle: 'left' }
          } else if (sourceNode.position.x > graphMidX && targetNode.position.x > graphMidX) {
            return { ...edge, sourceHandle: 'right', targetHandle: 'right' }
          } else {
            return { ...edge, sourceHandle: 'left', targetHandle: 'left' }
          }
        }
      }
      return edge
    })

    console.debug('[LAYOUT] Layout complete:', {
      totalNodes: adjustedNodes.length,
      totalEdges: adjustedEdges.length,
      terminalNodes: Array.from(terminalNodeIds),
    })

    return { nodes: adjustedNodes, edges: adjustedEdges }
  } catch (error) {
    console.error('[LAYOUT ERROR] ELK layout failed:', error)
    console.error('[LAYOUT ERROR] Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
    })
    console.warn(
      '[LAYOUT] Falling back to simple layout due to workflow issues. Check for invalid edges or missing nodes.'
    )

    // Provide a fallback simple layout when ELK fails
    const fallbackNodes = nodes.map((node, index) => ({
      ...node,
      position: {
        x: 100 + (index % 3) * 350, // Simple grid layout
        y: 100 + Math.floor(index / 3) * 150,
      },
    }))

    return { nodes: fallbackNodes, edges: deduplicatedEdges }
  }
}
